/**
 * 에러 모니터링(Sentry) — env-DSN 게이트 + 지연 로드.
 * REACT_APP_SENTRY_DSN 이 없으면 완전 no-op이고 @sentry/react 청크 자체를 받지 않는다
 * (메인 번들 영향 0 — DSN 설정 시에만 별도 청크로 로드). 운영은 Vercel 대시보드에 DSN 만 넣으면 켜진다.
 *
 * - init: window.onerror / onunhandledrejection 전역 핸들러를 Sentry 가 자동 설치(미처리 예외/거부 자동 캡처).
 * - captureError: RouteErrorBoundary·useListResource 등 화면이 삼킨 에러를 명시적으로 보낸다.
 * - 트레이싱(성능) 미사용 — 에러만. environment 는 REACT_APP_API_ENV 라벨.
 */
type SentryModule = typeof import("@sentry/react");

let sentry: SentryModule | null = null;

// [D2/D4] Sentry 이벤트 스크럽 — 제3자(Sentry)로 토큰/PII 가 새지 않도록 이벤트 전역 마스킹.
// - D4(실 보호): PII(이름/전화) search/keyword 쿼리가 breadcrumb·request URL 로 전송되는 누수 완화.
// - D2(심층방어): Bearer 토큰/authorization 헤더 마스킹. 현재 fetcher 들이 에러를 문자열화해 reject 하므로
//   raw AxiosError(config.headers.Authorization)가 Sentry 까지 가는 경로는 거의 없지만, 향후 raw-error
//   캡처가 생겨도 토큰이 새지 않도록 방어해 둔다.
const BEARER_RE = /Bearer\s+[A-Za-z0-9._~+/=-]+/g;
// ?search= / &keyword= 등 PII 우려 쿼리값 마스킹(키는 보존, 값만 가림)
const PII_QUERY_RE = /([?&](?:search|keyword|q|name|phone)=)[^&#\s]*/gi;

const maskString = (s: string): string =>
    s.replace(BEARER_RE, "Bearer [REDACTED]").replace(PII_QUERY_RE, "$1[REDACTED]");

const scrubValue = (value: unknown, seen: WeakSet<object>, depth: number): unknown => {
    if (depth > 8) return value; // 깊이 가드(과도한 재귀 방지)
    if (typeof value === "string") return maskString(value);
    if (Array.isArray(value)) return value.map((v) => scrubValue(v, seen, depth + 1));
    if (value && typeof value === "object") {
        if (seen.has(value as object)) return value; // 순환 가드
        seen.add(value as object);
        const obj = value as Record<string, unknown>;
        for (const k of Object.keys(obj)) {
            try {
                if (/^authorization$/i.test(k)) obj[k] = "[REDACTED]"; // 헤더 키 자체 redact
                else obj[k] = scrubValue(obj[k], seen, depth + 1);
            } catch {
                // 동결/쓰기불가 키는 건너뛴다 — 한 키 실패가 다른 키 마스킹을 막지 않도록(SDK 순서 의존성 제거)
            }
        }
    }
    return value;
};

/**
 * Sentry 이벤트에서 인증 토큰(Bearer)·PII 쿼리·Authorization 헤더를 마스킹(이벤트 전역, in-place).
 * beforeSend 에서 사용. 스크럽 자체 실패가 캡처를 막지 않도록 가드한다.
 */
export const scrubSentryEvent = <T>(event: T): T => {
    try {
        return scrubValue(event, new WeakSet<object>(), 0) as T;
    } catch {
        return event;
    }
};

export const initMonitoring = async (): Promise<void> => {
    const dsn = process.env.REACT_APP_SENTRY_DSN;
    if (!dsn) return; // DSN 미설정 → no-op, 청크 미로드

    try {
        const mod = await import("@sentry/react");
        mod.init({
            dsn,
            environment: process.env.REACT_APP_API_ENV || process.env.NODE_ENV || "unknown",
            tracesSampleRate: 0, // 트레이싱 미사용 — 에러만 수집
            sendDefaultPii: false,
            // [D2/D4] 토큰(Bearer)·PII 쿼리·Authorization 헤더가 Sentry 로 새지 않도록 이벤트 전역 스크럽
            beforeSend: (event) => scrubSentryEvent(event),
        });
        sentry = mod;
    } catch (e) {
        // 모니터링 초기화 실패가 앱을 막아선 안 된다.
        console.error("Sentry init 실패:", e);
    }
};

/**
 * 잡힌(=상위로 전파되지 않는) 에러를 모니터링에 보고한다.
 * 미설정/로드 전이면 no-op. 호출부의 기존 console.error 는 그대로 둔다(로컬 가시성 유지).
 */
export const captureError = (error: unknown, context?: Record<string, unknown>) => {
    if (!sentry) return;
    sentry.captureException(error, context ? { extra: context } : undefined);
};
